/**
 * RBAC-4.3 — remove someone's access now, restore it, and hand over administration.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. What the rows mean is `lib/personAccess.ts`; who may do what is the
 * database's (`20261102a2`). Nothing here passes a tenant id. Errors carry the server's sentence and
 * hint, which are written for the admin reading them.
 */

import { supabase } from '@/lib/supabaseClient';
import {
  HANDOVER_OPTIONS_RPC,
  HANDOVER_RPC,
  REMOVED_PEOPLE_RPC,
  REMOVE_PERSON_ACCESS_RPC,
  RESTORE_PERSON_ACCESS_RPC,
  handoverArgs,
  parseHandoverOptions,
  parseRemovedPeople,
  type HandoverOptions,
  type RemovedPerson,
} from '@/lib/personAccess';
import type { RpcErrorLike } from '@/lib/roleDuplication';

export class PersonAccessError extends Error {
  constructor(public readonly rpc: RpcErrorLike) {
    super(rpc.message ?? 'Unknown error');
  }
}

const toError = (e: { message?: string; code?: string; hint?: string }) =>
  new PersonAccessError({ message: e.message, code: e.code, hint: e.hint });

export async function removePersonAccess(userId: string): Promise<void> {
  const { error } = await supabase.rpc(REMOVE_PERSON_ACCESS_RPC, { p_user_id: userId });
  if (error) throw toError(error);
}

export async function restorePersonAccess(userId: string): Promise<void> {
  const { error } = await supabase.rpc(RESTORE_PERSON_ACCESS_RPC, { p_user_id: userId });
  if (error) throw toError(error);
}

export async function fetchRemovedPeople(): Promise<RemovedPerson[]> {
  const { data, error } = await supabase.rpc(REMOVED_PEOPLE_RPC);
  if (error) throw toError(error);
  return parseRemovedPeople(data);
}

export async function fetchHandoverOptions(): Promise<HandoverOptions> {
  const { data, error } = await supabase.rpc(HANDOVER_OPTIONS_RPC);
  if (error) throw toError(error);
  const parsed = parseHandoverOptions(data);
  if (!parsed) throw new PersonAccessError({ message: 'The handover options came back in a shape this screen does not read.' });
  return parsed;
}

export async function handOverAdministration(input: Parameters<typeof handoverArgs>[0]): Promise<void> {
  const { error } = await supabase.rpc(HANDOVER_RPC, handoverArgs(input));
  if (error) throw toError(error);
}
